// src/components/Home.js
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';  // Import useNavigate
import { Helmet } from 'react-helmet'; // Import Helmet
import { createTranslator, useLanguage } from '../i18n/LanguageContext';
import { homeTranslations } from '../i18n/translations';


const Home = () => {
  const navigate = useNavigate();  // Utilisation du hook useNavigate
  const [tournamentsCount, setTournamentsCount] = useState(0);  // Compteur pour le nombre de tournois
  const [eventsCount, setEventsCount] = useState(0);  // Compteur pour le nombre d'événements
  const { language } = useLanguage();
  const t = createTranslator(language, homeTranslations);

  useEffect(() => {
    // Initialisation des valeurs statiques
    setEventsCount(3000); // Valeur statique pour le nombre d'événements
    setTournamentsCount(250); // Valeur statique pour le nombre de tournois
  }, []);

  const handleViewTournaments = () => {
    navigate('/tournamentlist');  // Redirection vers la page tournamentlist
  };

  const handleViewLeague = () => {
    navigate('/leaguelist');  // Redirection vers la page leaguelist
  };

  const handleViewSubscriptions = () => {
    navigate('/abonnement');
  };

  return (
    <>
      {/* Petit bloc d'info (pour vérification) */}
      <div>
        <p>{t('eventCount', { count: eventsCount })}</p>
        <p>{t('tournamentCount', { count: tournamentsCount })}</p>
      </div>

      {/* Balises SEO */}
      <Helmet>
        <title>{t('pageTitle')}</title>
        <meta
          name="description"
          content="Bienvenue sur Padel Mendyx. Découvrez nos tournois et ligues de padel en Belgique. Rejoignez nos événements à Namur, Charleroi, et Brabant Wallon."
        />
        <meta
          name="keywords"
          content="tournois de padel, compétition padel Belgique, ligues padel Namur, Charleroi, Brabant Wallon, Padel Mendyx"
        />
        <meta property="og:title" content="Accueil | Tournois de Padel en Belgique - Padel Mendyx" />
        <meta
          property="og:description"
          content="Explorez nos tournois et ligues de padel. Découvrez les compétitions en Belgique avec Padel Mendyx."
        />
        <meta property="og:image" content="/logo2.jfif" />
        <meta property="og:url" content="https://padelmedyx.com/home" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Accueil | Tournois de Padel en Belgique - Padel Mendyx" />
        <meta
          name="twitter:description"
          content="Rejoignez nos événements de padel en Belgique avec Padel Mendyx. Découvrez les tournois et ligues."
        />
        <meta name="twitter:image" content="/logo2.jfif" />
        <meta name="language" content={language} />
        <meta name="geo.region" content="BE" />
        <meta name="author" content="Padel Mendyx" />
        <link rel="canonical" href="https://padelmedyx.com/home" />
      </Helmet>

      {/* Contenu principal */}
      <section className="bg-white text-black min-h-screen flex flex-col justify-between">
      <div className="container mx-auto text-center flex-grow py-8 md:py-16">          {/* Logo centré dans un cercle */}
          <img
            src="/logo2.jfif"
            loading="lazy"
            alt="Logo de Padel Mendyx, plateforme de tournois et ligues de padel en Belgique"
            className="
              mx-auto mb-8 
              h-36 w-36        /* Plus grand sur mobile */
              md:h-40 md:w-40  /* Taille moyenne sur écran md */
              lg:h-44 lg:w-44  /* Encore un peu plus grand sur écran lg */
              rounded-full 
              p-2 
              shadow-lg 
              transition-transform 
              duration-300 
              hover:scale-105
            "
          />

          {/* Texte léger et sobre */}
          <p className="text-lg mb-8 text-gray-600">
            {t('intro')}
          </p>

          {/* Boutons pour voir les tournois et les ligues */}
          <div className="flex justify-center space-x-4">
            <button
              onClick={handleViewTournaments}
              className="
                px-6 py-2 
                bg-[#4c2f8e] 
                text-white 
                font-medium 
                rounded-full 
                hover:bg-[#ea4c89] 
                transition 
                duration-300 
                transform 
                hover:scale-105
                md:px-4 md:py-2 
                lg:px-8 lg:py-3
              "
            >
              {t('viewTournaments')}
            </button>
            <button
              onClick={handleViewLeague}
              className="
                px-6 py-2 
                bg-[#4c2f8e] 
                text-white 
                font-medium 
                rounded-full 
                hover:bg-[#ea4c89] 
                transition 
                duration-300 
                transform 
                hover:scale-105
                md:px-4 md:py-2 
                lg:px-8 lg:py-3
              "
            >
              {t('viewLeagues')}
            </button>
          </div>

          {/* Bannière abonnements */}
          <div className="max-w-4xl mx-auto mt-6 mb-8 px-4">
  <div
    className="
      bg-gradient-to-r
      from-[#4c2f8e]
      to-[#5d39ab]
      rounded-2xl
      shadow-xl
      px-4
      py-4
      md:px-8
      md:py-6
      flex
      items-center
      justify-between
      relative
      overflow-hidden
    "
  >
    <div className="absolute right-0 top-0 opacity-10">
      <img
        src="/logo2.jfif"
        alt=""
        className="w-24 h-24 rounded-full"
      />
    </div>

    <div className="flex items-center gap-3 z-10 flex-1">
      <div className="text-3xl shrink-0">
        💰
      </div>

      <div>
        <h2 className="text-white font-bold text-sm md:text-xl leading-tight">
          {t('subscriptionTitle')}
        </h2>

        <p className="hidden md:block text-purple-100 text-sm mt-2">
          {t('subscriptionText')}
        </p>
      </div>
    </div>

    <button
      onClick={handleViewSubscriptions}
      className="
        z-10
        bg-white
        text-[#4c2f8e]
        font-semibold
        text-xs
        md:text-base
        px-3
        md:px-5
        py-2
        rounded-xl
        whitespace-nowrap
        hover:scale-105
        transition
      "
    >
      {t('view')}
    </button>
  </div>
</div>

          {/* Section avec les deux ronds (joueurs inscrits / tournois) */}
          <div className="mt-16 flex justify-center space-x-8 lg:space-x-24 flex-wrap">
            {/* Rond pour le nombre d'événements (joueurs inscrits) */}
            <div className="border-2 border-[#4c2f8e] rounded-full flex flex-col justify-center items-center shadow-lg h-40 w-40 sm:h-32 sm:w-32 lg:h-56 lg:w-56">
              <span className="text-[#4c2f8e] text-4xl sm:text-3xl lg:text-5xl font-light">
                {eventsCount}
              </span>
              <span className="text-[#4c2f8e] text-md sm:text-sm lg:text-lg mt-2">
                {t('registeredPlayers')}
              </span>
            </div>

            {/* Rond pour le nombre de tournois */}
            <div className="border-2 border-[#4c2f8e] rounded-full flex flex-col justify-center items-center shadow-lg h-40 w-40 sm:h-32 sm:w-32 lg:h-56 lg:w-56">
              <span className="text-[#4c2f8e] text-4xl sm:text-3xl lg:text-5xl font-light">
                +{tournamentsCount}
              </span>
              <span className="text-[#4c2f8e] text-md sm:text-sm lg:text-lg mt-2">
                {t('tournaments')}
              </span>
            </div>
          </div>
        </div>


        {/* Footer */}
        <footer className="bg-white border-t border-[#4c2f8e] shadow-sm">
        <div className="container mx-auto px-6 py-6 space-y-6">
  {/* Partenaires */}
  <section aria-labelledby="partners-title" className="border-t border-[#7c5ac2]/40 pt-6">
    <h2
      id="partners-title"
      className="text-center text-xs tracking-[0.25em] uppercase text-gray-500 mb-5"
    >
      {t('partners')}
    </h2>

    <ul className="flex flex-wrap justify-center items-center gap-2 md:gap-3 max-w-5xl mx-auto">
      {[
        {
          name: "Tennis Club Bois du Loup",
          href: "https://www.boisduloup.be/",
          img: "/BDL.jfif",
        },
        {
          name: "RETC Jumet",
          href: "https://www.facebook.com/RETCJUMET",
          img: "/JUMET.jfif",
        },
        {
          name: "333 Padel",
          href: "https://www.facebook.com/p/333-Padel-61556247308483/",
          img: "/333.jfif",
        },
        {
          name: "Adeps Le Mosane",
          href: "https://www.facebook.com/Centre.sportif.Adeps.La.Mosane/?locale=fr_FR",
          img: "/adeps.jfif",
        },
        {
          name: "Grez-Doiceau",
          href: "https://www.facebook.com/p/Hall-Omnisports-de-Grez-Doiceau-100063821912059/",
          img: "/grez.jfif",
        },
        {
          name: "Padel One",
          href: "http://www.padel-one.be/",
          img: "/padelone.jfif",
        },
        {
          name: "Padel Toro",
          href: "http://www.padeltoro.com/",
          img: "/toro.jfif",
        },
        {
          name: "High 5 Padel",
          href: "https://www.high5padel.com/",
          img: "/h5.jfif",
        },
        {
          name: "Zenith Padel Club",
          href: "https://www.facebook.com/p/Z%C3%A9nith-Padel-Club-61574162484898/",
          img: "/zenith.jfif",
        },

        {
          name: "Padel Papillons Ressaix",
          href: "https://padel-papillons.be/",
          img: "/ressaix.jfif",
        },
        {
          name: "Padel 1640",
          href: "",
          img: "/padel1640.jfif",
        },
        {
          name: "Garrincha",
          href: "https://www.garrincha.be/",
          img: "/garrincha.jfif",
        },
        {
          name: "Padel Smash Gembloutois",
          href: "",
          img: "/smash.jfif",
        },
        {
          name: "Padel Tero",
          href: "https://www.padeltero.be/",
          img: "/tero.jfif",
        },
        {
          name: "Padel Eghezée",
          href: "",
          img: "/padeleghezee.ico",
        },
        {
          name: "Mokko Padel Project",
          href: "",
          img: "/mokko.jfif",
        },
        {
          name: "Padel Events",
          href: "",
          img: "/padelevents.jfif",
        },
        {
          name: "Ciney Padel Club",
          href: "",
          img: "/ciney.jfif",
        },
      ].map((partner) => (
        <li key={partner.name}>
          <a
            href={partner.href || "#"}
            target="_blank"
            rel="noopener noreferrer"
            className="group shrink-0 flex-none"
            aria-label={partner.name}
          >
            <div className="w-10 h-10 md:w-11 md:h-11 rounded-xl bg-white ring-1 ring-black/10 p-1 grid place-items-center transition-all duration-300 hover:ring-[#4c2f8e]/30 hover:shadow-sm">
              <img
                src={partner.img}
                alt={partner.name}
                className="block max-w-full max-h-full object-contain object-center transition-transform duration-300 group-hover:scale-105"
                loading="lazy"
                decoding="async"
              />
            </div>
          </a>
        </li>
      ))}
    </ul>
  </section>

  {/* Liens légaux */}
  <nav
    aria-label={t('legalLinks')}
    className="flex flex-wrap justify-center gap-x-5 gap-y-2 text-sm text-gray-700"
  >
    <a
      href="/privacy-policy"
      className="hover:text-[#4c2f8e] transition-colors hover:underline underline-offset-4"
    >
      {t('privacy')}
    </a>

    <a
      href="/legal-mentions"
      className="hover:text-[#4c2f8e] transition-colors hover:underline underline-offset-4"
    >
      {t('legal')}
    </a>

    <a
      href="/cookies"
      className="hover:text-[#4c2f8e] transition-colors hover:underline underline-offset-4"
    >
      {t('cookies')}
    </a>

    <a
      href="/conditions-utilisation"
      className="hover:text-[#4c2f8e] transition-colors hover:underline underline-offset-4"
    >
      {t('terms')}
    </a>
  </nav>

  {/* Copyright */}
  <p className="text-[11px] text-gray-500 text-center leading-relaxed">
    © 2025 Padel Mendyx SRL – {t('rights')} {t('copyright')}
  </p>
</div>
</footer>

      </section>
    </>
  );
};

export default Home;
